/**
 * @fileoverview Lesson scoring weight configuration.
 *
 * **Single source of truth** for all lesson relevance scoring weights.
 *
 * To tune lesson matching, edit the values below. Each weight is documented
 * with its effect on the scoring algorithm. The `scoreAiBugfixLessonRecord`
 * function reads these weights at call time (merged with any runtime overrides
 * from `window.usheru.setLessonWeights()` or URL query params).
 *
 * ## Scoring signals
 *
 * | Signal             | Weight | Effect                                           |
 * |--------------------|--------|--------------------------------------------------|
 * | `sameApp`          | 4      | Bonus when lesson is from the same application   |
 * | `exactPath`        | 6      | Bonus when lesson path matches input path exactly |
 * | `partialPath`      | 3      | Bonus when one path contains the other           |
 * | `chainOverlap`     | 3      | Per matching token between input component chain and lesson text |
 * | `controlExact`     | 8      | Bonus when `memoryKey.control` appears verbatim in input text |
 * | `multiselectBonus` | 5      | Extra bonus for multiselect lessons (gated on `color-visibility` symptom) |
 * | `iconPositionBonus`| 6      | Extra bonus for icon-position lessons (gated on `position` symptom) |
 * | `expectedToken`    | 4      | Per token match from `expectedOutcome` text       |
 * | `longToken`        | 2      | Per token match (≥6 chars) from other sources     |
 * | `shortToken`       | 1      | Per token match (<6 chars) from other sources     |
 * | `minScore`         | 16     | Threshold — lessons scoring below this are excluded |
 *
 * ## Tuning guidance
 *
 * - **Too many irrelevant lessons?** Raise `minScore` or lower the
 *   special-case bonuses (`multiselectBonus`, `iconPositionBonus`).
 * - **Symptom-gated bonuses not helping?** Set the bonus to `0` to disable it.
 * - **Path dominates too much?** Lower `exactPath` / `partialPath`.
 * - **Token matching too noisy?** Lower `expectedToken`, `longToken`, `shortToken`.
 *
 * @packageDocumentation
 */

import type { LessonScoringWeights } from './types';

export const LESSON_SCORING_WEIGHTS: LessonScoringWeights = {
  sameApp: 4,
  exactPath: 6,
  partialPath: 3,
  chainOverlap: 3,
  controlExact: 8,
  multiselectBonus: 5,
  iconPositionBonus: 6,
  expectedToken: 4,
  longToken: 2,
  shortToken: 1,
  minScore: 16
};
